import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { doc, getDoc, updateDoc, collection, addDoc } from 'firebase/firestore';
import { LuFolderKanban, LuLoaderCircle, LuPlus, LuSave, LuWalletCards, LuX } from 'react-icons/lu';
import { db } from '../../../config/firebaseConfig';
import { TextInput } from '../../../components/form/FormControls';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import useEmployeeFinanceText from './employeeFinanceText';

const VISIT_TYPES = ['shortVisit', 'fullVisit', 'other'];

// Kaydedilen gider kayıtlarındaki adlar (Türkçe / kayıtlı değer olarak kalır)
const EXPENSE_NAMES = { shortVisit: 'Short Visit', fullVisit: 'Full Visit', other: 'Diğer' };

const ProjectCard = React.memo(({ project, budgetData = {}, onPricingChange, onTitleChange, onRemoveVisitType }) => {
  const { label } = useEmployeeFinanceText();
  const currency = project.currency || 'TRY';
  const isVisitTypeSelected = (visitType) => budgetData && budgetData[visitType] !== undefined;

  return (
    <section className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
      <h3 className="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">
        <LuFolderKanban aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-violet-500" />
        {project.titleShort || project.titleLong || label('pricing.untitled')}
      </h3>
      <div className="tw-grid tw-gap-2.5 sm:tw-grid-cols-3">
        {VISIT_TYPES.map((key) => {
          const typeLabel = label(`pricing.types.${key}`);
          if (!isVisitTypeSelected(key)) {
            return (
              <button
                key={key}
                type="button"
                onClick={() => onPricingChange(project.id, key, '')}
                className="tw-flex tw-h-full tw-min-h-[5.5rem] tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-rounded-xl tw-border tw-border-dashed tw-border-slate-300 tw-text-sm tw-font-medium tw-text-slate-500 tw-transition-colors hover:tw-border-violet-400 hover:tw-bg-violet-500/[0.05] hover:tw-text-violet-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-violet-500/40 dark:tw-border-white/15 dark:tw-text-slate-400 dark:hover:tw-text-violet-300"
              >
                <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
                {typeLabel}
              </button>
            );
          }
          const title = key === 'other' && budgetData[`${key}Title`] ? budgetData[`${key}Title`] : typeLabel;
          return (
            <div key={key} className="tw-space-y-2 tw-rounded-xl tw-border tw-border-violet-500/25 tw-bg-violet-500/[0.05] tw-p-3">
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                <span className="tw-min-w-0 tw-truncate tw-text-xs tw-font-semibold tw-text-violet-800 dark:tw-text-violet-200">{title}</span>
                <button
                  type="button"
                  onClick={() => onRemoveVisitType(project.id, key)}
                  title={label('pricing.remove')}
                  aria-label={label('pricing.remove')}
                  className="tw-grid tw-h-6 tw-w-6 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-slate-500 hover:tw-bg-rose-500/10 hover:tw-text-rose-600 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-rose-500/40"
                >
                  <LuX aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                </button>
              </div>
              {key === 'other' && (
                <TextInput
                  value={budgetData[`${key}Title`] || ''}
                  onChange={(event) => onTitleChange(project.id, key, event.target.value)}
                  placeholder={label('pricing.titlePlaceholder')}
                  aria-label={label('pricing.titleLabel')}
                  className="tw-h-9"
                />
              )}
              <div className="tw-relative">
                <TextInput
                  type="number"
                  min="0"
                  max="999999"
                  step="0.01"
                  inputMode="decimal"
                  value={budgetData[key] || ''}
                  onChange={(event) => onPricingChange(project.id, key, event.target.value)}
                  placeholder="0.00"
                  aria-label={label('pricing.amountLabel', { type: title })}
                  className="tw-h-9 tw-pr-14 tw-font-semibold tw-tabular-nums"
                />
                <span className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-xs tw-font-semibold tw-text-slate-400">{currency}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
});

ProjectCard.displayName = 'ProjectCard';
ProjectCard.propTypes = {
  project: PropTypes.object.isRequired,
  budgetData: PropTypes.object,
  onPricingChange: PropTypes.func.isRequired,
  onTitleChange: PropTypes.func.isRequired,
  onRemoveVisitType: PropTypes.func.isRequired,
};

/**
 * Çalışan Bütçe Ücretlendirme: per project of the employee, a price for a
 * short visit, a full visit and an optional named "other" item. Saving
 * stores them on the employee (budgetPricing) and creates an expense record
 * in Budgets for every filled price, as before.
 */
const BudgetPricingModal = ({ selectedEmployee, onClose, show }) => {
  const { language, label } = useEmployeeFinanceText();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [budgetPricing, setBudgetPricing] = useState({});

  const fetchEmployeeData = useCallback(async () => {
    if (!selectedEmployee || !selectedEmployee.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const employeeDoc = await getDoc(doc(db, 'Employees', selectedEmployee.id));

      if (!employeeDoc.exists()) {
        toast.error(label('pricing.notFound'));
        setLoading(false);
        return;
      }

      const employeeData = employeeDoc.data();
      const employeeProjects = employeeData.projects || [];

      if (employeeProjects.length === 0) {
        setProjects([]);
        setLoading(false);
        return;
      }

      setProjects(employeeProjects);
      setBudgetPricing(employeeData.budgetPricing || {});
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employee data:', error);
      toast.error(label('pricing.loadError'));
      setLoading(false);
    }
  }, [selectedEmployee, label]);

  useEffect(() => {
    if (show && selectedEmployee) fetchEmployeeData();
  }, [show, selectedEmployee, fetchEmployeeData]);

  const handlePricingChange = useCallback((projectId, visitType, value) => {
    // Empty string is accepted (the user may clear the field)
    if (value === '') {
      setBudgetPricing((prev) => ({ ...prev, [projectId]: { ...prev[projectId], [visitType]: '' } }));
      return;
    }
    const numValue = parseFloat(value);
    if (numValue < 0) return; // no negative values
    if (numValue > 999999) return; // upper limit
    setBudgetPricing((prev) => ({ ...prev, [projectId]: { ...prev[projectId], [visitType]: value } }));
  }, []);

  const handleTitleChange = useCallback((projectId, visitType, title) => {
    setBudgetPricing((prev) => ({ ...prev, [projectId]: { ...prev[projectId], [`${visitType}Title`]: title } }));
  }, []);

  const handleRemoveVisitType = useCallback((projectId, visitType) => {
    setBudgetPricing((prev) => {
      const projectData = { ...prev[projectId] };
      delete projectData[visitType];
      delete projectData[`${visitType}Title`];
      return { ...prev, [projectId]: projectData };
    });
  }, []);

  const handleSave = async () => {
    if (!selectedEmployee || !selectedEmployee.id) {
      toast.error(label('pricing.noEmployee'));
      return;
    }

    try {
      setSaving(true);
      const updatedBudgetPricing = {};
      const budgetExpensesToCreate = [];

      Object.keys(budgetPricing).forEach((projectId) => {
        const projectBudget = budgetPricing[projectId];

        updatedBudgetPricing[projectId] = {
          ...projectBudget,
          updatedAt: new Date(),
          updatedBy: 'admin', // TODO: Gerçek kullanıcı bilgisi eklenebilir
        };

        // An expense record per filled visit type
        const visitTypes = [
          { key: 'shortVisit', label: EXPENSE_NAMES.shortVisit },
          { key: 'fullVisit', label: EXPENSE_NAMES.fullVisit },
          { key: 'other', label: projectBudget.otherTitle || EXPENSE_NAMES.other },
        ];

        visitTypes.forEach(({ key, label: expenseName }) => {
          const amount = projectBudget[key];
          if (amount && parseFloat(amount) > 0) {
            budgetExpensesToCreate.push({
              projectId,
              BütçeAdı: expenseName,
              BütçeKaynak: 'Çalışan Bütçe Ücretlendirme',
              BütçeSahibi: `${selectedEmployee.eName} ${selectedEmployee.eSurname}`,
              BütçeTipi: 'Gider',
              BütçeKategorisi: 'Personel',
              GiderTipi: 'Personel Gideri',
              ParaBirimi: 'TRY', // TODO: Proje para birimini kullan
              KDV: '0',
              BütçeMiktarı: parseFloat(amount),
              BütçeTarihi: new Date().toISOString().split('T')[0],
              BeklenenÖdemeTarihi: '',
              ÖdemeDurumu: 'Beklemede',
              AktifDurum: 'Aktif',
              createdAt: new Date().toISOString(),
              createdBy: 'Çalışan Bütçe Ücretlendirme Modülü',
            });
          }
        });
      });

      // Employee record
      await updateDoc(doc(db, 'Employees', selectedEmployee.id), { budgetPricing: updatedBudgetPricing });

      // Expense records in Budgets
      let addedExpensesCount = 0;
      for (const expense of budgetExpensesToCreate) {
        await addDoc(collection(db, 'Budgets'), expense);
        addedExpensesCount++;
      }

      if (addedExpensesCount > 0) toast.success(label('pricing.savedWithExpenses', { count: addedExpensesCount }));
      else toast.success(label('pricing.saved'));

      setSaving(false);
      onClose();
    } catch (error) {
      console.error('Error saving budget pricing:', error);
      toast.error(label('pricing.saveError'));
      setSaving(false);
    }
  };

  const employeeName = `${selectedEmployee?.eName || ''} ${selectedEmployee?.eSurname || ''}`.trim();

  let body;
  if (loading) {
    body = (
      <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-10 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
        <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
        {label('common.loading')}
      </p>
    );
  } else if (projects.length === 0) {
    body = (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-10 tw-text-center">
        <LuWalletCards aria-hidden="true" className="tw-h-8 tw-w-8 tw-text-slate-300 dark:tw-text-slate-600" />
        <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('pricing.noProjects')}</p>
      </div>
    );
  } else {
    body = (
      <div className="tw-space-y-3">
        <p className="tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{label('pricing.note')}</p>
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            budgetData={budgetPricing[project.id]}
            onPricingChange={handlePricingChange}
            onTitleChange={handleTitleChange}
            onRemoveVisitType={handleRemoveVisitType}
          />
        ))}
      </div>
    );
  }

  return (
    <Modal
      isOpen={Boolean(show)}
      onClose={onClose}
      size="lg"
      dismissible={!saving}
      title={label('pricing.title')}
      subtitle={employeeName}
      closeLabel={label('common.cancel')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30" size="xs">
          <LuWalletCards aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{label('common.cancel')}</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {saving ? label('common.saving') : label('common.save')}
          </Button>
        </>
      )}
    >
      {body}
    </Modal>
  );
};

BudgetPricingModal.propTypes = {
  selectedEmployee: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  show: PropTypes.bool,
};

export default BudgetPricingModal;
